/**
 * 地図閲覧ログを自動収集するユーティリティ
 *
 * 使い方:
 * import { startMapViewLogging } from '@/utils/mapViewLogger'
 * startMapViewLogging(map)
 */

let logTimer = null
let lastPosition = null
let viewStartTime = null
let isLogging = false

/**
 * 地図閲覧ログの記録を開始
 * @param {maplibregl.Map} map - MapLibre GLのマップインスタンス
 * @param {Object} options - オプション
 * @param {number} options.interval - ログ送信間隔（ミリ秒、デフォルト: 30000 = 30秒）
 * @param {boolean} options.enabled - ログ記録の有効/無効（デフォルト: true）
 */
export function startMapViewLogging(map, options = {}) {
  const {
    interval = 30000, // 30秒ごと
    enabled = true,
  } = options

  if (!enabled || isLogging) {
    return
  }

  isLogging = true
  viewStartTime = Date.now()

  // 初期位置を記録
  const center = map.getCenter()
  const zoom = map.getZoom()
  lastPosition = {
    lat: center.lat,
    lng: center.lng,
    zoom: Math.round(zoom),
  }

  // 定期的にログを送信
  logTimer = setInterval(() => {
    sendViewLog()
  }, interval)

  // 地図移動時に位置を更新
  map.on('moveend', () => {
    const center = map.getCenter()
    const zoom = map.getZoom()

    // 位置が大きく変わったら前の位置のログを送信
    const latDiff = Math.abs(center.lat - lastPosition.lat)
    const lngDiff = Math.abs(center.lng - lastPosition.lng)
    const zoomDiff = Math.abs(Math.round(zoom) - lastPosition.zoom)

    // 緯度経度が0.01度以上、またはズームレベルが2以上変化したら
    if (latDiff > 0.01 || lngDiff > 0.01 || zoomDiff >= 2) {
      sendViewLog()

      // 新しい位置を記録
      lastPosition = {
        lat: center.lat,
        lng: center.lng,
        zoom: Math.round(zoom),
      }
      viewStartTime = Date.now()
    }
  })

  // ページを離れる時に最後のログを送信
  window.addEventListener('beforeunload', () => {
    sendViewLog()
  })
}

/**
 * 地図閲覧ログの記録を停止
 */
export function stopMapViewLogging() {
  if (!isLogging) {
    return
  }

  if (logTimer) {
    clearInterval(logTimer)
    logTimer = null
  }

  // 最後のログを送信
  sendViewLog()

  isLogging = false
}

/**
 * 現在の閲覧ログをサーバーに送信
 */
async function sendViewLog() {
  if (!lastPosition || !viewStartTime) {
    return
  }

  const now = Date.now()
  const duration = Math.floor((now - viewStartTime) / 1000) // 秒単位

  // 最低5秒以上見ていた場合のみ記録
  if (duration < 5) {
    return
  }

  // ズームレベル10以上のみ記録（確固たる意志を持った閲覧のみ）
  if (lastPosition.zoom < 10) {
    return
  }

  const formData = new FormData()
  formData.append('action', 'log')
  formData.append('lat', lastPosition.lat.toFixed(6))
  formData.append('lng', lastPosition.lng.toFixed(6))
  formData.append('zoom', lastPosition.zoom)
  formData.append('duration', duration)

  try {
    const response = await fetch('/oh3/php/mapViewLogs.php', {
      method: 'POST',
      body: formData,
    })

    const data = await response.json()

    if (data.success) {
      // logged successfully
    } else {
      console.error('[MapViewLogger] Error:', data.error)
    }
  } catch (error) {
    console.error('[MapViewLogger] Failed to send log:', error)
  }

  // タイマーをリセット
  viewStartTime = now
}

/**
 * 手動でログを送信（デバッグ用）
 */
export function flushViewLog() {
  sendViewLog()
}
